/**
 * @name MenuColumns
 * @description Componente para gestionar las columnas del reporte
 * @author Ricardo Guerra
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import i18n from 'i18next';
import styled from 'styled-components';

// Material-UI
import MuiList from '@material-ui/core/List';
import MuiListItem from '@material-ui/core/ListItem';
import MuiListItemIcon from '@material-ui/core/ListItemIcon';
import MuiListItemText from '@material-ui/core/ListItemText';

import { Flex, MUI, Unicons, Theme, Typography } from '@styles';

const MenuColumns = (props) => {
  const { columns, onHideColumn } = props;

  const [anchorMenu, setAnchorMenu] = useState(null);

  const openMenu = (event) => {
    setAnchorMenu(event.currentTarget);
  };

  const closeMenu = () => {
    setAnchorMenu(null);
  };

  return (
    <>
      <MUI.Tooltip title={i18n.t('Report.columns')}>
        <MUI.ButtonIcon onClick={openMenu}>
          <Unicons.Grids size="20" />
        </MUI.ButtonIcon>
      </MUI.Tooltip>

      <Popover
        open={Boolean(anchorMenu)}
        anchorEl={anchorMenu}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Flex.Col padding="16px" style={{ borderBottom: `1px solid ${Theme.colors.greyLight}` }}>
          <Typography.H6>{i18n.t('Report.columns')}</Typography.H6>
          <Typography.Text>{i18n.t('Report.selectColumnsToShow')}</Typography.Text>
        </Flex.Col>

        <List component="nav" aria-label="column-list">
          {/* Columnas */}
          {columns.map((column, index) => (
            <ListItem
              button
              key={index}
              disabled={!column.enableHiding}
              onClick={() => onHideColumn(column)}
            >
              <ListItemIcon>
                {!column.hidden ? <Unicons.Check /> : <Unicons.Close />}
              </ListItemIcon>
              <ListItemText primary={column.header} />
            </ListItem>
          ))}
        </List>
      </Popover>
    </>
  );
};

MenuColumns.propTypes = {
  // Colección de columnas a mostrar en el grid. 
  columns: PropTypes.array.isRequired,
  // Función que se ejecuta cuando se cambia la visibilidad de una columna.
  onHideColumn: PropTypes.func
};

// *************************************
// STYLED COMPONENTS                               
// *************************************

const Popover = styled(MUI.Popover)`
  .MuiPopover-paper {
    border-radius: 6px;
    box-shadow: ${(props) => props.theme.shadows.depth4};
  }
`;

const List = styled(MuiList)`
  &.MuiList-padding {
    padding-top: 8px;
    padding-bottom: 8px;
    max-height: 450px;
    overflow-y: auto;
  }
`;

const ListItem = styled(MuiListItem)`
  &.MuiListItem-root {
    min-height: 40px;
    padding: 0px 16px;
  }

  & > a {
    text-decoration: none;
  }
`;

const ListItemIcon = styled(MuiListItemIcon)`
 &.MuiListItemIcon-root {
   min-width: 0;
   padding-right: 16px;
   color: ${(props) => props.theme.colors.greyDark};
 }

 .MuiSvgIcon-root {
   font-size: 20px;
 }
`;

const ListItemText = styled(MuiListItemText)`
  .MuiTypography-root {
    font-family: 'Bryant Pro Regular';
    font-variant: tabular-nums;
    font-size: ${(props) => props.theme.typography.bodyText};
    font-weight: normal;
    line-height: 1.3;
    letter-spacing: 0.0175em;
    color: ${(props) => props.theme.colors.greyDark};
    text-decoration: none;
  }
`;

export default MenuColumns;